// Percorso file: C:\APPQPSC\src\utils\exportCsv.js
//
// ⚠️ PAY-SEASON / FETTA 2 — L'EXPORT È PER-ANNATA. Le quote non si leggono
// più dal campo piatto `fees` (che è la sola stagione 2026) ma dall'annata
// GUARDATA dall'elenco, con la stessa sede unica — `quoteDellAnnata` — che
// usano le colonne, le card e la finestra Payments. Senza l'anno, un CSV
// scaricato guardando il 2027 avrebbe riportato i conti del 2026 accanto ai
// soci del 2027, e nulla nel file l'avrebbe detto.
//
// ⚠️ D-ADMIN1 (RD-151): ROLE e TEAM NON LEGGONO PIÙ I CAMPI SENZ'ANNO.
// `user.team` era un campo morto (la squadra vive sulle tessere da F3b) e
// usciva «-» quasi per tutti; `user.role` era il ruolo grezzo («Manager»).
// Le due colonne ora sono ciò che l'elenco MOSTRA nelle sue colonne Role e
// Team, per l'annata guardata: le due funzioni arrivano dal chiamante
// (UsersList, `getDisplayRoles` / `getTeamInfo`), così la regola resta in
// un posto solo e il file non può dire una cosa diversa dallo schermo.
// Chi non ha tessere di squadra ha la cella vuota: non si inventa nulla.
//
// ⚠️ OGNI CELLA TRA VIRGOLETTE, le virgolette interne raddoppiate (RFC 4180):
// una nota di pagamento «cash, partial» resta in una cella sola. Tocca SOLO
// la forma del file esportato: nessun dato cambia.

import { quoteDellAnnata, totaleVersato } from './quote';

// Una cella CSV: sempre tra virgolette, `"` interne raddoppiate. Il vuoto
// (null/undefined) resta vuoto, come faceva `join` prima.
const cella = (valore) =>
  `"${String(valore ?? '').replace(/"/g, '""')}"`;

// ⚠️ R9.8 — UNA CELLA DI TESTO NON DEVE DIVENTARE UNA FORMULA. Se comincia con
// = + - @ (o TAB, CR, LF) il foglio di calcolo la leggerebbe come formula: le
// si antepone un apostrofo (la difesa standard anti-CSV-injection), poi passa
// da `cella` come tutte. Solo le colonne di TESTO: le tre dei soldi restano
// numeri, e il Balance negativo (un credito) comincia legittimamente con «-».
const INIZIO_DI_FORMULA = /^[=+\-@\t\r\n]/;
const cellaDiTesto = (valore) => {
  const testo = String(valore ?? '');
  return cella(INIZIO_DI_FORMULA.test(testo) ? `'${testo}` : testo);
};

// Total Due, Total Paid, Balance: le posizioni delle colonne numeriche.
const COLONNE_NUMERICHE = new Set([5, 6, 7]);

/**
 * @param {Array} users i soci già filtrati e ordinati dall'elenco
 * @param {number|null} anno l'annata guardata («Season Viewed»)
 * @param {object} colonne le rese a schermo dell'elenco, `(user, anno) => string[]`
 * @param {Function} colonne.ruoli la colonna Role (`getDisplayRoles`)
 * @param {Function} colonne.squadre la colonna Team (`getTeamInfo`)
 */
export function exportFeesToCSV(users, anno, { ruoli, squadre }) {
  const rows = [
    ['First Name', 'Last Name', 'Email', 'Role', 'Team', 'Total Due', 'Total Paid', 'Balance', 'Payments']
  ];

  users.forEach(user => {
    // Annata ignota → record vuoto, cioè zeri e nessun pagamento: è la stessa
    // guardia dello schermo, e per la stessa ragione — su un'annata che
    // nessuno ha scelto non si inventano conti.
    const quote = quoteDellAnnata(user, anno);
    const totalDue = quote.totalDue || 0;
    const payments = quote.payments;
    const totalPaid = totaleVersato(quote);
    const balance = totalDue - totalPaid;
    const paymentsStr = payments.map(p => `${p.date}: $${p.amount} (${p.note || 'no note'})`).join(' | ');

    rows.push([
      user.firstName,
      user.lastName,
      user.email,
      // Stesso separatore dello schermo, dove le voci oltre la prima si
      // leggono unite da « · ».
      ruoli(user, anno).join(' · '),
      squadre(user, anno).join(' · '),
      totalDue.toFixed(2),
      totalPaid.toFixed(2),
      balance.toFixed(2),
      paymentsStr
    ]);
  });

  const csvContent = rows
    .map(e => e.map((v, i) => (COLONNE_NUMERICHE.has(i) ? cella(v) : cellaDiTesto(v))).join(','))
    .join('\n');
  // BOM UTF-8 in testa, una volta sola: Excel su Windows legge così gli
  // accenti e il « · » invece di indovinare la codifica.
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  // L'annata guardata nel nome: il CSV del 2026 e quello del 2027 non sono
  // più indistinguibili. Annata ignota → il nome di sempre, senza inventarne una.
  const suffisso = Number.isInteger(anno) ? `_${anno}` : '';
  link.setAttribute('download', `QPSC_fees_report${suffisso}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
